import React, { useEffect, useState } from "react";
import { useCompany } from "../../context/CompanyContext";
import { ensurePayrollBootstrap, getPayrollDashboard } from "../payrollService";
import PayrollSummaryCard from "../components/PayrollSummaryCard";
import PayrollTableEmpty from "../components/PayrollTableEmpty";

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function PayrollDashboard() {
  const { companyId } = useCompany();
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [stats, setStats] = useState({
    activeEmployees: 0,
    recentRuns: [],
    recentPeriods: [],
    componentCount: 0,
  });

  useEffect(() => {
    async function load() {
      if (!companyId) return;
      setLoading(true);
      setMsg("");
      try {
        await ensurePayrollBootstrap(companyId);
        const data = await getPayrollDashboard(companyId);
        setStats(data);
      } catch (err) {
        setMsg(err.message || "Failed to load payroll dashboard");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [companyId]);

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert error">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <PayrollSummaryCard
          title="Active Employees"
          value={loading ? "..." : stats.activeEmployees}
          subtitle="Currently eligible for payroll"
        />
        <PayrollSummaryCard
          title="Pay Components"
          value={loading ? "..." : stats.componentCount}
          subtitle="Earnings, deductions and employer items"
        />
        <PayrollSummaryCard
          title="Recent Runs"
          value={loading ? "..." : stats.recentRuns.length}
          subtitle="Latest payroll run activity"
        />
        <PayrollSummaryCard
          title="Recent Periods"
          value={loading ? "..." : stats.recentPeriods.length}
          subtitle="Configured payroll periods"
        />
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Recent Payroll Runs</h3>
          </div>
          {!stats.recentRuns?.length ? (
            <PayrollTableEmpty title="No payroll runs yet" />
          ) : (
            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Run Name</th>
                    <th>Status</th>
                    <th>Gross</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentRuns.map((row) => (
                    <tr key={row.id}>
                      <td>{row.run_name}</td>
                      <td>
                        <span className={`pill ${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>{formatMoney(row.gross_total)}</td>
                      <td>{formatMoney(row.net_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Recent Payroll Periods</h3>
          </div>
          {!stats.recentPeriods?.length ? (
            <PayrollTableEmpty title="No payroll periods yet" />
          ) : (
            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Payment Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentPeriods.map((row) => (
                    <tr key={row.id}>
                      <td>{row.period_name}</td>
                      <td>
                        <span className={`pill ${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.payment_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
